import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, performanceBenefits, features, CONTACT_INFO } from '../data/mock';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Package, Sparkles, Award, CheckCircle2 } from 'lucide-react';

const featureIcons = [Package, Truck, RotateCcw, ShieldCheck];
const benefitIcons = [Award, Sparkles, Package, CheckCircle2];

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'precision-3d-printer') || products[0];
  const gridSlugs = ['premium-gel-pen-set','wireless-mouse','high-definition-streaming-webcam','compact-multi-drawer-desk-organizer','office-chair-with-lumbar-support','executive-leather-desk-chair','contemporary-office-wall-art-collection','compact-paper-shredder'];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);

  useEffect(() => { document.title = 'Desk Line Supply | Premium Office Supplies & Workspace Essentials'; }, []);

  return (
    <div className="min-h-screen bg-background text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Hero — full-bleed image with overlay */}
        <section className="relative overflow-hidden">
          <img src={siteImages.heroMain} alt="Modern organized workspace" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-950/90 via-dark-950/70 to-dark-950/20" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur text-white text-xs font-bold uppercase tracking-[0.18em] rounded-full mb-6 border border-white/15"><Sparkles className="w-3.5 h-3.5 text-power-400" /> Everything for a better workday</span>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.03]">A workspace that <span className="text-power-400">works</span> as hard as you do</h1>
              <p className="mt-5 text-lg text-dark-200 max-w-xl">Desk Line Supply curates dependable supplies, electronics and furniture that keep your desk organized and your day productive &mdash; with free USA shipping on every order.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/shop" className="inline-flex items-center gap-2 rounded-lg bg-athletic-600 px-8 py-4 font-semibold text-white transition hover:bg-athletic-700 shadow-button">Shop Office Supplies <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/about" className="inline-flex items-center gap-2 rounded-lg border border-white/30 px-8 py-4 font-semibold text-white hover:bg-white/10 transition">Our Story</Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-dark-200">
                <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-power-400" /> Free USA shipping</span>
                <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-power-400" /> 30-day returns</span>
                <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-power-400" /> Secure checkout</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="bg-white border-b border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = featureIcons[i % featureIcons.length];
              return (
                <div key={f.id} className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-lg bg-athletic-50 text-athletic-600 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></div>
                  <div><p className="font-semibold text-dark-900 text-sm">{f.title}</p><p className="text-dark-500 text-xs mt-0.5">{f.description}</p></div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Bestsellers */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">Handpicked for your desk</span><h2 className="font-display text-4xl font-semibold text-dark-900">Best-selling essentials</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-athletic-700 hover:gap-2.5 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{productsGrid.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
          </div>
        </section>

        {/* Categories — image-top cards */}
        <section className="bg-white py-16 sm:py-20 border-y border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10"><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">Shop by category</span><h2 className="font-display text-4xl font-semibold text-dark-900">Built around how you work</h2></div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
              {categories.map((category) => (
                <Link key={category.id} to={`/shop?category=${category.slug}`} className="group bg-background rounded-xl overflow-hidden border border-dark-100 hover:border-athletic-200 hover:shadow-card-hover transition-all">
                  <div className="aspect-[4/3] bg-dark-50 overflow-hidden"><img src={category.image} alt={category.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-4">
                    <h3 className="font-display text-base font-semibold text-dark-900 group-hover:text-athletic-600 transition-colors leading-tight">{category.name}</h3>
                    <p className="text-xs text-dark-400 mt-1">{category.productCount} product{category.productCount > 1 ? 's' : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Flagship promo split (image right) */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-3">Featured this week</span>
              <h2 className="font-display text-4xl font-semibold text-dark-900 mb-4">{featuredProduct.name}</h2>
              <p className="text-dark-500 mb-5 max-w-lg leading-relaxed">{featuredProduct.description.slice(0, 190)}&hellip;</p>
              <p className="text-3xl font-bold text-athletic-600 mb-7">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-athletic-600 text-white rounded-lg font-semibold hover:bg-athletic-700 transition shadow-button">Buy now</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border border-dark-300 text-dark-800 rounded-lg font-medium hover:border-athletic-500 hover:text-athletic-600 transition">Browse more</Link>
              </div>
            </div>
            <div className="order-first lg:order-last rounded-2xl bg-white border border-dark-100 shadow-card p-8"><img src={featuredProduct.image} alt={featuredProduct.name} className="w-full max-w-md mx-auto object-contain" /></div>
          </div>
        </section>

        {/* Benefits band (emerald) */}
        <section className="bg-gradient-athletic py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {performanceBenefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={b.id} className="text-center sm:text-left">
                    <div className="w-12 h-12 rounded-lg bg-white/15 flex items-center justify-center text-white mb-4 mx-auto sm:mx-0"><Icon className="w-6 h-6" /></div>
                    <h3 className="font-display text-xl font-semibold text-white">{b.title}</h3>
                    <p className="text-white/85 text-sm mt-1">{b.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">How it works</span><h2 className="font-display text-4xl font-semibold text-dark-900">Set up your nest in four steps</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="bg-background border border-dark-100 rounded-xl p-6 shadow-card"><span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-athletic-600 text-white font-display font-semibold">{step.id}</span><h3 className="font-display text-xl font-semibold text-dark-900 mt-4">{step.title}</h3><p className="text-dark-500 text-sm mt-2">{step.description}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background py-16 sm:py-20 border-t border-dark-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-4xl font-semibold text-dark-900 text-center mb-10">Questions, answered</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'How fast will my order arrive?', a: `Orders ship via USPS within 1 business day and typically arrive in 2 to 4 business days. Need an update? Call us at ${CONTACT_INFO.phone}.` },
                { q: 'Do you charge for shipping?', a: 'Never. Every order ships free via USPS Standard Shipping within the USA, with no minimum spend.' },
                { q: 'What is your return policy?', a: 'You have 30 days from delivery to start a return. Reach out and our team will walk you through each step.' },
                { q: 'How can I get in touch?', a: `Our support team is here at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (<div key={i} className="bg-white rounded-xl p-6 border border-dark-100 shadow-card"><h3 className="font-display text-xl font-semibold text-dark-900">{faq.q}</h3><p className="mt-2 text-dark-600">{faq.a}</p></div>))}
            </div>
            <div className="text-center mt-10"><Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold">See all FAQs <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
